import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const CollectionContext = createContext();

export function useCollection() {
    return useContext(CollectionContext);
}

export function CollectionProvider({ children }) {
    // Kinks Data
    const [kinks, setKinks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalKinks, setTotalKinks] = useState(0);
    const [isKinksLoaded, setIsKinksLoaded] = useState(false);
    const [kinksLoading, setKinksLoading] = useState(false);
    const [kinksError, setKinksError] = useState(null);

    // Sex Actions Data
    const [sexActions, setSexActions] = useState([]);
    const [isActionsLoaded, setIsActionsLoaded] = useState(false);
    const [actionsLoading, setActionsLoading] = useState(false);
    const [actionsError, setActionsError] = useState(null);

    // Arousal Mechanics Data
    const [mechanics, setMechanics] = useState([]);
    const [totalMechanics, setTotalMechanics] = useState(0);
    const [isMechanicsLoaded, setIsMechanicsLoaded] = useState(false);
    const [mechanicsLoading, setMechanicsLoading] = useState(false);
    const [mechanicsError, setMechanicsError] = useState(null);

    // Arousal Effects Data
    const [effects, setEffects] = useState([]);
    const [totalEffects, setTotalEffects] = useState(0);
    const [isEffectsLoaded, setIsEffectsLoaded] = useState(false);
    const [effectsLoading, setEffectsLoading] = useState(false);
    const [effectsError, setEffectsError] = useState(null);

    // Scenarios Data
    const [scenarios, setScenarios] = useState([]);
    const [totalScenarios, setTotalScenarios] = useState(0);
    const [isScenariosLoaded, setIsScenariosLoaded] = useState(false);
    const [scenariosLoading, setScenariosLoading] = useState(false);
    const [scenariosError, setScenariosError] = useState(null);

    // Load Categories (Lightweight, can be done once)
    const loadCategories = useCallback(async () => {
        try {
            const cats = await api.getCategories();
            setCategories(cats);
        } catch (err) {
            console.error('Failed to load categories:', err);
        }
    }, []);

    // Fetch Kinks (Supports params for search/filter)
    const loadKinks = useCallback(async (params = {}, page = 1, forceRefresh = false) => {
        // Return cached if available and standard request (no params, page 1)
        if (isKinksLoaded && !forceRefresh && Object.keys(params).length === 0 && page === 1) {
            return { kinks: kinks, pagination: { totalItems: totalKinks } };
        }

        // Only show loading spinner on initial load, not on refetch
        if (kinks.length === 0) {
            setKinksLoading(true);
        }
        setKinksError(null);
        try {
            const data = await api.getKinks({ ...params, page });

            // Defensive guards for API failures
            const kinksData = data?.kinks || [];
            const totalItems = data?.pagination?.totalItems || 0;

            if (page === 1) {
                setKinks(kinksData);
            } else {
                setKinks(prev => [...prev, ...kinksData]);
            }

            setTotalKinks(totalItems);
            setIsKinksLoaded(true);
            return data || { kinks: [], pagination: { totalItems: 0 } };
        } catch (err) {
            setKinksError(err.message);
            throw err;
        } finally {
            setKinksLoading(false);
        }
    }, [isKinksLoaded, kinks, totalKinks]);

    // CRUD for Kinks to keep local state in sync
    const addKink = (newKink) => {
        setKinks(prev => [newKink, ...prev]);
        setTotalKinks(prev => prev + 1);
    };

    const updateKink = (updatedKink) => {
        setKinks(prev => prev.map(k => k.id === updatedKink.id ? updatedKink : k));
    };

    const deleteKink = (kinkId) => {
        setKinks(prev => prev.filter(k => k.id !== kinkId));
        setTotalKinks(prev => prev - 1);
    };

    // Fetch Sex Actions
    const loadSexActions = useCallback(async (params = {}, forceRefresh = false) => {
        if (isActionsLoaded && !forceRefresh && Object.keys(params).length === 0) {
            return; // Return cached if available and standard request
        }

        // Only show loading spinner on initial load, not on refetch
        if (sexActions.length === 0) {
            setActionsLoading(true);
        }
        setActionsError(null);
        try {
            const data = await api.getSexActions(params);
            setSexActions(data.actions || []);
            setIsActionsLoaded(true);
        } catch (err) {
            setActionsError(err.message);
        } finally {
            setActionsLoading(false);
        }
    }, [isActionsLoaded]);

    // Fetch Arousal Mechanics
    const loadMechanics = useCallback(async (params = {}, page = 1, forceRefresh = false) => {
        if (isMechanicsLoaded && !forceRefresh && Object.keys(params).length === 0 && page === 1) {
            return { mechanics, pagination: { totalItems: totalMechanics } };
        }

        // Only show loading spinner on initial load, not on refetch
        if (mechanics.length === 0) {
            setMechanicsLoading(true);
        }
        setMechanicsError(null);
        try {
            const data = await api.getArousalMechanics({ ...params, page });
            const mechanicsData = data?.mechanisms || data?.mechanics || [];
            if (page === 1) {
                setMechanics(mechanicsData);
            } else {
                setMechanics(prev => [...prev, ...mechanicsData]);
            }
            setTotalMechanics(data?.pagination?.totalItems || 0);
            setIsMechanicsLoaded(true);
            return data;
        } catch (err) {
            setMechanicsError(err.message);
            throw err;
        } finally {
            setMechanicsLoading(false);
        }
    }, [isMechanicsLoaded, mechanics, totalMechanics]);

    // Fetch Arousal Effects
    const loadEffects = useCallback(async (params = {}, page = 1, forceRefresh = false) => {
        if (isEffectsLoaded && !forceRefresh && Object.keys(params).length === 0 && page === 1) {
            return { effects, pagination: { totalItems: totalEffects } };
        }

        // Only show loading spinner on initial load, not on refetch
        if (effects.length === 0) {
            setEffectsLoading(true);
        }
        setEffectsError(null);
        try {
            const data = await api.getArousalEffects({ ...params, page });
            const effectsData = data?.effects || [];
            if (page === 1) {
                setEffects(effectsData);
            } else {
                setEffects(prev => [...prev, ...effectsData]);
            }
            setTotalEffects(data?.pagination?.totalItems || 0);
            setIsEffectsLoaded(true);
            return data;
        } catch (err) {
            setEffectsError(err.message);
            throw err;
        } finally {
            setEffectsLoading(false);
        }
    }, [isEffectsLoaded, effects, totalEffects]);

    // Fetch Scenarios
    const loadScenarios = useCallback(async (params = {}, page = 1, forceRefresh = false) => {
        if (isScenariosLoaded && !forceRefresh && Object.keys(params).length === 0 && page === 1) {
            return { scenarios, pagination: { totalItems: totalScenarios } };
        }

        // Only show loading spinner on initial load, not on refetch
        if (scenarios.length === 0) {
            setScenariosLoading(true);
        }
        setScenariosError(null);
        try {
            const data = await api.getScenarios({ ...params, page });
            const scenariosData = data?.scenarios || [];
            if (page === 1) {
                setScenarios(scenariosData);
            } else {
                setScenarios(prev => [...prev, ...scenariosData]);
            }
            setTotalScenarios(data?.pagination?.totalItems || 0);
            setIsScenariosLoaded(true);
            return data;
        } catch (err) {
            setScenariosError(err.message);
            throw err;
        } finally {
            setScenariosLoading(false);
        }
    }, [isScenariosLoaded, scenarios, totalScenarios]);

    // Pre-cache Data on Mount
    React.useEffect(() => {
        loadCategories();
        loadKinks();
        loadSexActions();
        loadMechanics();
        loadEffects();
        loadScenarios();
    }, []);


    const value = {
        // Kinks
        kinks,
        categories,
        totalKinks,
        isKinksLoaded,
        kinksLoading,
        kinksError,
        loadKinks,
        loadCategories,
        addKink,
        updateKink,
        deleteKink,

        // Actions
        sexActions,
        isActionsLoaded,
        actionsLoading,
        actionsError,
        loadSexActions,

        // Mechanics
        mechanics,
        totalMechanics,
        isMechanicsLoaded,
        mechanicsLoading,
        mechanicsError,
        loadMechanics,

        // Effects
        effects,
        totalEffects,
        isEffectsLoaded,
        effectsLoading,
        effectsError,
        loadEffects,

        // Scenarios
        scenarios,
        totalScenarios,
        isScenariosLoaded,
        scenariosLoading,
        scenariosError,
        loadScenarios
    };

    return (
        <CollectionContext.Provider value={value}>
            {children}
        </CollectionContext.Provider>
    );
}
